import { PalmettoAutocomplete } from '@palmetto/dsm-components';
import React, { useMemo, useCallback } from 'react';

import { FlagCriteriaFilterType, FlagReason, RequirementFlagSettingsDocument } from 'types';

export interface ChecklistFlagCriteriaSectionProps {
  setting: RequirementFlagSettingsDocument | undefined;
  isLoadingSetting: boolean;
  accountState: string | undefined;
  selectedCriterionId: string | null;
  selectedProblems: { label: string; value: string }[];
  selectedReason: FlagReason | null;
  onCriterionChange: (id: string | null) => void;
  onProblemsChange: (problems: { label: string; value: string }[]) => void;
  onReasonChange: (reason: FlagReason | null) => void;
}

const REASON_ITEMS = Object.values(FlagReason).map((r) => ({ label: r, value: r }));

export const ChecklistFlagCriteriaSection: React.FC<ChecklistFlagCriteriaSectionProps> = ({
  setting,
  isLoadingSetting,
  accountState,
  selectedCriterionId,
  selectedProblems,
  selectedReason,
  onCriterionChange,
  onProblemsChange,
  onReasonChange,
}) => {
  const availableCriteria = useMemo(() => {
    const criteria = setting?.acceptanceCriteria ?? [];
    if (!setting?.isFilterable || !accountState) return criteria;
    return criteria.filter((criterion) => {
      const filters = criterion.filters ?? [];
      if (filters.length === 0) return true;
      return filters.every((f) => f.type === FlagCriteriaFilterType.addressState && f.value === accountState);
    });
  }, [setting, accountState]);

  const selectedCriterion = useMemo(
    () => availableCriteria.find((c) => c.id.toString() === selectedCriterionId) ?? null,
    [availableCriteria, selectedCriterionId],
  );

  const problemOptions = useMemo(() => {
    if (!selectedCriterion) return [];
    return selectedCriterion.problems
      .flatMap((problem) =>
        problem.details && problem.details.length > 0
          ? problem.details.map((detail) => ({
              label: `${problem.displayText} - ${detail}`,
              value: `${problem.id.toString()}-${detail}`,
            }))
          : [{ label: problem.displayText, value: problem.id.toString() }],
      )
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [selectedCriterion]);

  const reasonItems = useMemo(
    () =>
      REASON_ITEMS.map((item) =>
        Object.assign({}, item, {
          selected: item.value === selectedReason,
        }),
      ).toSorted((a, b) => a.label.localeCompare(b.label)),
    [selectedReason],
  );

  const handleReasonChange = useCallback(
    (details: { value: string[] }) => {
      onReasonChange((details.value[0] as FlagReason) ?? null);
    },
    [onReasonChange],
  );

  const handleCriterionClick = useCallback(
    (criterionId: string) => {
      if (criterionId === selectedCriterionId) {
        onCriterionChange(null);
      } else {
        onCriterionChange(criterionId);
      }
      onProblemsChange([]);
    },
    [selectedCriterionId, onCriterionChange, onProblemsChange],
  );

  const handleCriterionButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const criterionId = event.currentTarget.dataset.criterionId;
      if (!criterionId) return;
      handleCriterionClick(criterionId);
    },
    [handleCriterionClick],
  );

  const handleProblemClick = useCallback(
    (opt: { label: string; value: string }) => {
      const isSelected = selectedProblems.some((p) => p.value === opt.value);
      if (isSelected) {
        onProblemsChange(selectedProblems.filter((p) => p.value !== opt.value));
      } else {
        onProblemsChange([...selectedProblems, opt]);
      }
    },
    [selectedProblems, onProblemsChange],
  );

  const handleProblemButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const value = event.currentTarget.dataset.problemValue;
      if (!value) return;
      const opt = problemOptions.find((problemOption) => problemOption.value === value);
      if (!opt) return;
      handleProblemClick(opt);
    },
    [problemOptions, handleProblemClick],
  );

  if (isLoadingSetting) {
    return <span className="text-[12px] text-text-secondary">Loading...</span>;
  }

  if (!setting) return null;

  const hasCriteria = (setting.acceptanceCriteria?.length ?? 0) > 0;

  if (!hasCriteria) {
    return <PalmettoAutocomplete label="Reason" items={reasonItems} onValueChange={handleReasonChange} />;
  }

  if (availableCriteria.length === 0) return null;

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <span className="text-[12px] font-bold text-text-secondary">Acceptance Criteria</span>
        <div className="flex flex-wrap gap-6">
          {availableCriteria.map((criterion) => {
            const isSelected = criterion.id.toString() === selectedCriterionId;
            return (
              <button
                key={criterion.id.toString()}
                type="button"
                data-criterion-id={criterion.id.toString()}
                onClick={handleCriterionButtonClick}
                className={`rounded-10 border-2 px-12 py-6 text-[12px] font-bold transition-colors ${
                  isSelected
                    ? 'border-tag-text-default-volt bg-tag-container-default-volt text-tag-text-default-volt'
                    : 'border-border-default bg-transparent text-text-primary hover:border-tag-text-default-volt'
                }`}
              >
                {criterion.displayText}
              </button>
            );
          })}
        </div>
      </div>

      {selectedCriterion && problemOptions.length > 0 && (
        <div className="flex flex-col gap-6">
          <span className="text-[12px] font-bold text-text-secondary">Issue(s)</span>
          <div className="flex flex-wrap gap-6">
            {problemOptions.map((opt) => {
              const isSelected = selectedProblems.some((p) => p.value === opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-problem-value={opt.value}
                  onClick={handleProblemButtonClick}
                  className={`rounded-10 border-2 px-12 py-6 text-[12px] font-bold transition-colors ${
                    isSelected
                      ? 'border-btn-bg-primary-idle bg-btn-bg-primary-idle text-btn-content-primary-idle'
                      : 'border-border-default bg-transparent text-text-primary hover:border-btn-bg-primary-idle'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
